/**
 * Stable `#` targets for every card on the page.
 *
 * The page is eleven thousand pixels tall. Without anchors there is no way to
 * link to a step, no way to come back to one, and nothing for a menu to point
 * at — so every card claims an id derived from its own words.
 *
 * ## Why the id comes from the words and not from the index
 *
 * `#step-7` breaks the moment a card is inserted above it, and this timeline
 * has had cards inserted above it repeatedly. A slug of the card's own heading
 * survives reordering, and when a heading is edited the old link breaks
 * loudly rather than silently pointing at someone else's card.
 *
 * ## Why this is duplicated in Python
 *
 * `api/app/prerender.py` writes the same ids into the server-rendered body.
 * The two must agree exactly: the body is served before the bundle runs, so a
 * deep link that resolves against the prerendered markup and then again
 * against the hydrated DOM has to land in the same place both times. The
 * Python half carries a comment pointing back here.
 */

/** Lowercase, punctuation to single hyphens, trimmed. Must match `_slug` in
 *  `api/app/prerender.py` character for character. */
export function slug(...parts: (string | undefined | null)[]): string {
  const joined = parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    // Keep letters and digits; everything else becomes a separator. Accented
    // characters are decomposed first so "café" is `cafe`, not `caf`.
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return joined || 'card'
}

/** Is this id already spoken for by another card?
 *
 *  Only ids this module claimed count. Core renders the blocks and *then*
 *  removes the prerendered body, so for a moment every server-rendered `<li>`
 *  is still in the document holding the very id its client twin is about to
 *  ask for. Counting those pushed every card to `-2` and broke the parity with
 *  the served markup that the ids exist to keep. A server-rendered node has no
 *  `data-skin-anchor`, so this ignores it and matches it instead. */
function taken(id: string, self: HTMLElement): boolean {
  const other = document.getElementById(id)
  return Boolean(other && other !== self && other.dataset.skinAnchor)
}

/**
 * Give an element an id and mark it as a menu target.
 *
 * De-duplicated against the live document rather than against a counter this
 * module keeps, because blocks re-render in edit mode and a counter would
 * drift a suffix further on every save.
 */
export function claimAnchor(el: HTMLElement, label: string, eyebrow?: string): string {
  const base = `s-${slug(label || eyebrow || 'card')}`
  let id = base
  for (let n = 2; taken(id, el); n += 1) id = `${base}-${n}`
  el.id = id
  // What the menu reads. Kept on the element so the menu is built from what is
  // actually on the page rather than from a second list that can disagree
  // with it.
  el.dataset.skinAnchor = label
  if (eyebrow) el.dataset.skinAnchorEyebrow = eyebrow
  return id
}
